import { defineStore } from 'pinia'

const STORAGE_KEY = 'selected_stock'

const normalize = (stock) => ({
  stock_id: stock.stock_id,
  name: stock.name,
  code: stock.code,
})

const readStoredStocks = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []

    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return parsed.filter((stock) => stock && stock.code).map(normalize)
    }
    // The filter was single-select before checkboxes landed, so an existing
    // key still holds one bare object -- carry that selection over instead of
    // silently dropping it on the first load after the upgrade.
    return parsed && parsed.code ? [normalize(parsed)] : []
  } catch (error) {
    // A hand-edited or half-written localStorage value must not take the
    // dashboard down on boot -- fall back to "no stock filter".
    return []
  }
}

export const stockStore = defineStore({
  id: 'stock',
  state: () => ({
    // Empty = no stock filter, i.e. the dashboard shows the whole portfolio
    // for whatever platforms are selected. Whole {stock_id, name, code} rows
    // are kept (not just ids) so the navbar can label the active filter
    // without waiting on the stock list to load.
    selectedStocks: readStoredStocks(),
  }),
  getters: {
    selectedStockCodes: (state) => state.selectedStocks.map((stock) => stock.code),
    isStockSelected: (state) => (stock) =>
      state.selectedStocks.some((entry) => String(entry.code) === String(stock.code)),
  },
  actions: {
    setStocks(stocks) {
      const value = (Array.isArray(stocks) ? stocks : [])
        .filter((stock) => stock && stock.code)
        .map(normalize)

      if (value.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }

      this.selectedStocks = value
    },
    toggleStock(stock) {
      if (!stock || !stock.code) return

      const remaining = this.selectedStocks.filter(
        (entry) => String(entry.code) !== String(stock.code)
      )
      // Same length back means it wasn't selected, so this click adds it.
      const next =
        remaining.length === this.selectedStocks.length
          ? [...this.selectedStocks, stock]
          : remaining

      this.setStocks(next)
    },
    clearStocks() {
      this.setStocks([])
    },
  },
})
